// src/components/discovery/ScanBanner.jsx
// Animated banner shown while a discovery run is active.
// Polls GET /api/discovery/runs every 2s and reports completion.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { motion, AnimatePresence } from 'framer-motion';
import { getDiscoveryRuns } from '../../api/discovery';
import { FS, RAIL_WIDTH, hoverOnly } from '../../theme/tokens';
import { sourceTierLabel } from '../../utils/labels';

function useElapsed(startedAt) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [startedAt]);
  return elapsed;
}

function formatElapsed(s) {
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

// ── Pulsing radar dot ─────────────────────────────────────────────────────────
const PulseDot = ({ color = 'success.main' }) => (
  <Box sx={{ position: 'relative', width: 12, height: 12, flexShrink: 0 }}>
    {/* Expanding ring */}
    <Box
      component={motion.div}
      animate={{ scale: [1, 2.2], opacity: [0.6, 0] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeOut' }}
      sx={{
        position: 'absolute', inset: 0,
        borderRadius: '50%',
        bgcolor: color,
      }}
    />
    {/* Solid core */}
    <Box
      component={motion.div}
      animate={{ scale: [1, 0.85, 1] }}
      transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
      sx={{
        position: 'absolute', inset: 0,
        borderRadius: '50%',
        bgcolor: color,
      }}
    />
  </Box>
);

// ── Main banner ───────────────────────────────────────────────────────────────
export default function ScanBanner({ sourceTier, onDone, onDismiss }) {
  const startedAt      = useRef(Date.now());
  const elapsed        = useElapsed(startedAt.current);
  const [done, setDone]           = useState(false);
  const [newCount, setNewCount]   = useState(null);
  const [visible, setVisible]     = useState(true);

  // Poll runs endpoint every 2s while scanning
  useEffect(() => {
    if (done) return;
    const interval = setInterval(async () => {
      try {
        const runs = await getDiscoveryRuns();
        // Find the most recent run for our tier
        const match = runs.find(r => r.source_tier === sourceTier);
        if (match && match.status !== 'running') {
          clearInterval(interval);
          setNewCount(match.candidates_new ?? null);
          setDone(true);
          onDone?.({ candidates_new: match.candidates_new });
          // Auto-dismiss after 4s
          setTimeout(() => {
            setVisible(false);
            setTimeout(onDismiss, 350); // wait for exit animation
          }, 4000);
        }
      } catch {
        // ignore polling errors
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [done, sourceTier, onDone, onDismiss]);

  const tierLabel = sourceTierLabel(sourceTier);

  return (
    <AnimatePresence>
      {visible && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: done ? 'success.light' : 'divider',
            bgcolor: done ? 'success.50' : 'background.paper',
            // subtle left accent
            borderLeft: `${RAIL_WIDTH}px solid`,
            borderLeftColor: done ? 'success.main' : 'primary.main',
            transition: 'border-color 0.3s ease, background-color 0.3s ease',
          }}
        >
          {/* Status indicator */}
          {done ? (
            <CheckCircleOutlineIcon
              sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }}
            />
          ) : (
            <PulseDot color="primary.main" />
          )}

          {/* Scanning animation — rows of dots while active */}
          {!done && (
            <Box sx={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
              {[0, 1, 2].map(i => (
                <Box
                  key={i}
                  component={motion.div}
                  animate={{ scaleY: [1, 2.2, 1], opacity: [0.4, 1, 0.4] }}
                  transition={{
                    duration: 0.9,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.18,
                  }}
                  sx={{
                    width: 3,
                    height: 10,
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                    transformOrigin: 'center',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Text */}
          <Typography sx={{ fontSize: FS.sm, fontWeight: 500, flex: 1 }}>
            {done ? (
              <>
                {tierLabel} scan complete
                {newCount != null && (
                  <Typography
                    component="span"
                    sx={{ fontSize: FS.sm, color: 'text.secondary', ml: 0.75 }}
                  >
                    — {newCount} new candidate{newCount !== 1 ? 's' : ''} found
                  </Typography>
                )}
              </>
            ) : (
              <>
                Scanning {tierLabel}
                <Typography
                  component="span"
                  sx={{ fontSize: FS.sm, color: 'text.secondary', ml: 0.75 }}
                >
                  {formatElapsed(elapsed)}
                </Typography>
              </>
            )}
          </Typography>

          {/* Dismiss */}
          <Tooltip title="Dismiss">
            <IconButton
              size="small"
              onClick={() => {
                setVisible(false);
                setTimeout(onDismiss, 350);
              }}
              sx={{ color: 'text.disabled', ...hoverOnly({ color: 'text.secondary' }) }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </AnimatePresence>
  );
}
